import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BookOpen, Bookmark, Award, ArrowRight, Play, Clock, Highlighter } from "lucide-react";

export default function Dashboard() {
  const [progress, setProgress] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);
  const [recent, setRecent] = useState([]);
  const [highlightsCount, setHighlightsCount] = useState(0);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/progress").then((r) => setProgress(r.data)).catch(() => {});
    api.get("/bookmarks").then((r) => setBookmarks(r.data)).catch(() => {});
    api.get("/activity/recent").then((r) => setRecent(r.data)).catch(() => {});
    api.get("/highlights").then((r) => setHighlightsCount(r.data.length)).catch(() => {});
  }, []);

  const totalCompleted = progress?.completed?.length || 0;
  const totalChapters = progress?.modules?.reduce((s, m) => s + m.total, 0) || 0;
  const overallPct = totalChapters > 0 ? Math.round((totalCompleted / totalChapters) * 100) : 0;

  const resumeTarget = recent[0];
  const otherRecent = recent.slice(1, 4);

  return (
    <div className="max-w-6xl mx-auto px-6 py-14">
      <div className="mb-12">
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] mb-3">Your dashboard</div>
        <h1 className="font-serif text-5xl font-black text-[#0A0A0A] tracking-tight">
          Hello, {user?.name || "friend"}.
        </h1>
      </div>

      {/* Resume where you left off */}
      {resumeTarget && (
        <section data-testid="resume-widget" className="mb-14 border border-[#0A0A0A] bg-[#FFFFFF]">
          <div className="grid md:grid-cols-12 gap-0">
            <div className="md:col-span-8 p-8 border-r border-[#E5E5E0]">
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-4">
                <Play className="w-3.5 h-3.5" /> Resume where you left off
              </div>
              <div className="font-mono text-xs uppercase tracking-wider text-[#525252] mb-1">
                {resumeTarget.module_number} · {resumeTarget.module_title}
              </div>
              <h2 className="font-serif text-3xl lg:text-4xl font-bold text-[#0A0A0A] tracking-tight leading-tight mb-2">
                {resumeTarget.chapter_title}
              </h2>
              <div className="flex items-center gap-3 text-xs font-mono uppercase tracking-wider text-[#525252] mb-6">
                <span>Chapter {resumeTarget.chapter_number}</span>
                <span>·</span>
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {resumeTarget.reading_time} min</span>
              </div>
              <Link
                data-testid="resume-cta"
                to={`/modules/${resumeTarget.module_id}/chapters/${resumeTarget.chapter_id}`}
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#0A0A0A] text-[#FFFFFF] hover:bg-[#000000] transition-colors font-medium btn-primary-hover"
              >
                Continue reading <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="md:col-span-4 p-8 bg-[#F5F5F0]">
              <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] mb-4">Recently opened</div>
              {otherRecent.length === 0 ? (
                <p className="text-sm text-[#525252]">No other recent chapters.</p>
              ) : (
                <ul className="space-y-3">
                  {otherRecent.map((r) => (
                    <li key={`${r.module_id}-${r.chapter_id}`}>
                      <Link
                        data-testid={`recent-${r.chapter_id}`}
                        to={`/modules/${r.module_id}/chapters/${r.chapter_id}`}
                        className="block group"
                      >
                        <div className="font-serif font-semibold text-[#0A0A0A] group-hover:text-[#000000] leading-snug">{r.chapter_title}</div>
                        <div className="text-[10px] font-mono uppercase tracking-wider text-[#525252] mt-0.5">
                          {r.module_number} · {r.chapter_number}
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Stats bento */}
      <div className="grid md:grid-cols-12 gap-0 border-t border-l border-[#E5E5E0] mb-14">
        <div className="md:col-span-8 border-r border-b border-[#E5E5E0] p-8">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-[#525252] mb-4">
            <BookOpen className="w-3.5 h-3.5" /> Overall reading progress
          </div>
          <div className="flex items-baseline gap-3 mb-4">
            <div className="font-serif text-6xl font-black text-[#0A0A0A]">{overallPct}%</div>
            <div className="text-[#525252] text-sm">{totalCompleted} of {totalChapters} chapters</div>
          </div>
          <div className="progress-rail"><div className="progress-fill" style={{ width: `${overallPct}%` }} /></div>
        </div>

        <div className="md:col-span-2 border-r border-b border-[#E5E5E0] p-8">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-[#525252] mb-4">
            <Bookmark className="w-3.5 h-3.5" /> Bookmarks
          </div>
          <div className="font-serif text-5xl font-black text-[#0A0A0A] mb-2">{bookmarks.length}</div>
          <Link data-testid="dashboard-view-bookmarks" to="/bookmarks" className="text-xs font-mono uppercase tracking-wider text-[#0A0A0A] hover:text-[#C5A059] inline-flex items-center gap-1">
            View <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        <div className="md:col-span-2 border-r border-b border-[#E5E5E0] p-8">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-[#525252] mb-4">
            <Highlighter className="w-3.5 h-3.5" /> Highlights
          </div>
          <div data-testid="dashboard-highlights-count" className="font-serif text-5xl font-black text-[#0A0A0A]">{highlightsCount}</div>
        </div>
      </div>

      {/* Per-track progress */}
      {["foundations", "india", "dubai"].map((trackId) => {
        const trackModules = (progress?.modules || []).filter((m) => m.track === trackId);
        if (trackModules.length === 0) return null;
        const label = trackId === "foundations" ? "Foundations" : trackId === "india" ? "India Track" : "Dubai Track";
        const chipCls = trackId === "india" ? "bg-[#C5A059] text-[#0A0A0A]" : trackId === "dubai" ? "bg-white text-[#0A0A0A] border border-[#0A0A0A]" : "bg-[#0A0A0A] text-white";
        return (
          <section key={trackId} className="mb-10" data-testid={`dashboard-track-${trackId}`}>
            <div className="flex items-center gap-3 mb-4">
              <span className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${chipCls}`}>{label}</span>
              <span className="font-mono text-[10px] uppercase tracking-wider text-[#525252]">{trackModules.length} modules</span>
            </div>
            <div className="border-t border-l border-[#E5E5E0]">
              {trackModules.map((m) => (
                <Link
                  key={m.module_id}
                  data-testid={`dashboard-module-${m.module_id}`}
                  to={`/modules/${m.module_id}`}
                  className="flex items-center gap-6 p-6 border-r border-b border-[#E5E5E0] hover:bg-[#F5F5F0] transition-colors"
                >
                  <div className="font-mono text-2xl text-[#C5A059] w-16">{m.number}</div>
                  <div className="flex-1">
                    <div className="font-serif text-xl font-semibold text-[#0A0A0A] mb-2">{m.title}</div>
                    <div className="progress-rail max-w-md"><div className="progress-fill-gold progress-fill" style={{ width: `${m.percent}%` }} /></div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-[#525252] mt-2">
                      {m.completed}/{m.total} chapters · {m.percent}%
                    </div>
                  </div>
                  {m.percent === 100 && (
                    <Link
                      to={`/certificate/${m.module_id}`}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`dashboard-cert-${m.module_id}`}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs font-mono uppercase tracking-wider text-[#0A0A0A] border border-[#C5A059] hover:bg-[#C5A059] transition-colors"
                    >
                      <Award className="w-3.5 h-3.5" /> Certificate
                    </Link>
                  )}
                </Link>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
